import { alpha, CSSObject, Divider, Drawer, styled, Theme } from '@mui/material';
import { grey, orange } from '@mui/material/colors';
import {
	getNavDrawerCollapsedWidth,
	NAV_BAR_DRAWER_RIGHT_SPACE,
	NAV_BAR_DRAWER_RIGHT_SPACE_COLLAPSED,
	NAV_BAR_LIST_ITEM_HEIGHT,
	NAV_DRAWER_WIDTH,
} from 'shared/_components/app-layout/constants';
import { IconLogo } from 'shared/_ui/icon';
import configs from 'shared/constants/configs';

const POINTER_SIZE = Math.sqrt(NAV_BAR_LIST_ITEM_HEIGHT ** 2 / 2);

export const NavLinkListItemCorner = styled('div', {
	shouldForwardProp: (prop) => prop !== 'expanded',
})<{ expanded: boolean }>(({ expanded }) => ({
	display: 'none',
	'.active > &': {
		display: 'block',
		filter: 'drop-shadow(0px 0px 6px rgba(0, 0, 0, 0.2))',
		position: 'absolute',
		right: '-17px',
		backgroundColor: 'white',
		transform: `translateY(-50%) ${expanded ? '' : 'scaleX(0.5)'} rotate(45deg)`,
		transition: 'transform 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
		width: POINTER_SIZE,
		height: POINTER_SIZE,
		top: '50%',
		// zIndex: '-1',
		clipPath: 'path("M -21 -20 L 55 0 L 54 55")',
	},
}));

export const NavDrawerLogoIcon = styled(IconLogo, {
	shouldForwardProp: (propName: PropertyKey) => propName !== 'expanded',
})<{ expanded: boolean }>(({ theme, expanded }) => ({
	color: configs.suffix ? orange[500] : theme.palette.common.white,
	height: 60,
	width: 60,
	transition: `height 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms,
							  width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms`,
	[theme.breakpoints.up('sm')]: {
		height: expanded ? 60 : 30,
		width: expanded ? 60 : 30,
	},
}));

export const NavDrawerHeader = styled('div')<{ expanded: boolean }>(({ theme, expanded }) => ({
	width: '100%',
	flexShrink: 0,
	padding: theme.spacing(2),
	paddingTop: theme.spacing(6),
	textAlign: 'center',
	position: 'sticky',
	zIndex: 1,
	background: theme.palette.common.black,
	top: 0,
	[theme.breakpoints.up('sm')]: {
		'&:after': {
			width: expanded ? NAV_BAR_DRAWER_RIGHT_SPACE : NAV_BAR_DRAWER_RIGHT_SPACE_COLLAPSED,
			right: expanded ? -NAV_BAR_DRAWER_RIGHT_SPACE : -NAV_BAR_DRAWER_RIGHT_SPACE_COLLAPSED,
			content: '""',
			height: '100%',
			position: 'absolute',
			top: 0,
			background: theme.palette.common.white,
			zIndex: 0,
		},
	},
	// // necessary for content to be below app bar
	// ...theme.mixins.toolbar,
}));

export const NavDrawerHeaderBackground = styled('div')(({ theme }) => ({
	content: '""',
	width: '100%',
	height: '100px',
	position: 'absolute',
	top: 0,
	background: theme.palette.common.white,
	zIndex: 0,
}));

export const openedMixin = (theme: Theme): CSSObject => ({
	width: NAV_DRAWER_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	// overflowX: 'hidden',
});

export const closedMixin = (theme: Theme): CSSObject => ({
	width: NAV_DRAWER_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	// overflowX: 'hidden',
	[theme.breakpoints.up('sm')]: {
		width: getNavDrawerCollapsedWidth(theme),
	},
});

export const NavDrawer = styled(Drawer, { shouldForwardProp: (prop) => prop !== 'expanded' })<{
	expanded: boolean;
}>(({ theme, expanded, open }) => ({
	flexShrink: 0,
	'&.MuiDrawer-root .MuiDrawer-paper': {
		backgroundColor: 'transparent',
		// '&::-webkit-scrollbar-thumb': {
		// 	background: alpha(theme.palette.common.white, 0.25),
		// },
		// '&::-webkit-scrollbar-track': {
		// 	background: grey[900],
		// },
		paddingInlineStart: 0,
		// TODO: replace transform: 'scaleX(-1)' with `scale: -1 0` in 104 chrome
		// transform: 'scaleX(-1)', // Move scroll to the left
		alignItems: 'flex-end',
		[theme.breakpoints.down('sm')]: {
			boxShadow: 'none',
		},
	},
	...(expanded && {
		...openedMixin(theme),
		'& .MuiDrawer-paper': openedMixin(theme),
	}),
	...(!expanded && {
		...closedMixin(theme),
		'& .MuiDrawer-paper': closedMixin(theme),
	}),
}));

export const NavDrawerInner = styled('div', { shouldForwardProp: (prop) => prop !== 'expanded' })<{
	expanded: boolean;
}>(({ theme, expanded }) => ({
	flex: 1,
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	// transform: 'scaleX(-1)', // Move scroll to the left
	// width: `calc(100% - ${
	// 	expanded ? NAV_BAR_DRAWER_RIGHT_SPACE : NAV_BAR_DRAWER_RIGHT_SPACE_COLLAPSED
	// }px)`,
	backgroundColor: theme.palette.common.black,
}));

export const NavDrawerDivider = styled(Divider)(({ theme }) => ({
	borderColor: theme.palette.common.white,
}));

export const Div = styled('div')();
